import React, { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { getGeneric } from '../../../common/constants'
import { getMerchant } from '../../../helpers/ReduxHelpers'
import { ContributionRow } from './RtoContributions'
import { rtoBand3 } from '../rtoBand'

// Gauge colour by risk score (higher = redder).
const scoreColor = (v) => {
  if (v >= 75) return 'error'
  if (v >= 42) return 'warning'
  return 'success'
}

// Advisory action per band. NETRA is advisory only — it never holds/cancels.
const ACTION = {
  RED: 'Confirm the order or convert to prepaid before dispatch.',
  ORANGE: 'Confirm delivery or offer a prepaid link — safe to dispatch in parallel.',
  YELLOW: 'Monitor — tag for NDR priority if it slips.',
  GREEN: 'Standard processing — no action needed.',
  GREY: 'New buyer — limited history; treat with pincode/address risk in mind.',
}

/**
 * RTO Risk card for the merchant order-detail page. Mirrors the admin panel card.
 *
 * `netra` is the summary on the order payload (order.netra_rto = {score, band,
 * scoredAt}) — renders the gauge + band immediately. Buyer history, hygiene
 * status and the full per-parameter weightage lazy-fetch from
 * GET /api/order/{id}/netra-score (rtoScoreResponse). Shown by default; hidden
 * only when the merchant is explicitly opted out (showNetraScoreInMerchantPanel
 * === false). Renders nothing when unscored.
 */
const RtoRiskCard = ({ netra, orderId, scorePath, scoredStageNote }) => {
  const [resp, setResp] = useState(null)

  useEffect(() => {
    let active = true
    if (!orderId || !netra) return undefined
    // Channel orders live at a sibling endpoint; `scorePath` lets the same
    // card serve both without a fork. Defaults to the order endpoint.
    getGeneric(scorePath || `order/${orderId}/netra-score`)
      .then((res) => {
        if (!active) return
        const payload = res?.data?.rtoScoreResponse
        if (payload) setResp(payload)
      })
      .catch(() => {
        /* breakdown is best-effort — gauge + band still render */
      })
    return () => {
      active = false
    }
  }, [orderId, netra, scorePath])

  // Shown by default; hidden only when the merchant is explicitly opted out.
  if (getMerchant()?.showNetraScoreInMerchantPanel === false) return null
  if (!netra || netra.score === null || netra.score === undefined) return null
  const score = Number(netra.score)
  if (Number.isNaN(score)) return null

  const b3 = rtoBand3(netra.band)
  // Full per-parameter weightage (every feature, sorted by |impact| desc), lazy-loaded.
  const contributions = Array.isArray(resp?.all_contributions) ? resp.all_contributions : []
  const hygieneBlock = resp?.ml_contribution === 0
  // Buyer delivery history (display-only evidence). null / known_buyer=false when
  // the buyer is unresolved (masked phone, first-time buyer, or no merchant_id).
  const buyer = resp?.buyer_history || null

  return (
    <Card sx={{ boxShadow: 1 }}>
      <CardHeader
        title='RTO Risk'
        action={
          <Tooltip title='RTO likelihood scored by NETRA'>
            <Typography variant='caption' color='text.secondary' sx={{ mt: 1, mr: 1 }}>
              Powered by NETRA
            </Typography>
          </Tooltip>
        }
      />
      <CardContent>
        {scoredStageNote && (
          // Channel-order scores are frozen at ingest and are NOT re-derived from
          // the fulfilled shipment, so the same buyer/address can legitimately show
          // a different score on /orders. Without this line that divergence reads
          // as a bug.
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1.5 }}>
            {scoredStageNote}
          </Typography>
        )}
        <Stack direction='row' spacing={3} alignItems='center'>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress variant='determinate' value={100} size={92} thickness={4} sx={{ color: (t) => t.palette.grey[200] }} />
            <CircularProgress
              variant='determinate'
              value={Math.max(0, Math.min(100, score))}
              size={92}
              thickness={4}
              color={scoreColor(score)}
              sx={{ position: 'absolute', left: 0 }}
            />
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant='h5' fontWeight={700}>{Math.round(score)}</Typography>
              <Typography variant='caption' color='text.secondary'>/100</Typography>
            </Box>
          </Box>
          <Box>
            <Chip color={b3.color} label={b3.key === 'NEW' ? 'New buyer' : `${b3.label} risk`} sx={{ fontWeight: 600 }} />
            {netra.scoredAt && (
              <Typography variant='caption' display='block' color='text.secondary' sx={{ mt: 1 }}>
                Scored {new Date(netra.scoredAt).toLocaleString('en-IN')}
              </Typography>
            )}
          </Box>
        </Stack>

        {buyer && (
          <Box sx={{ mt: 2 }}>
            <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', letterSpacing: '.04em' }}>
              Buyer history
            </Typography>
            {buyer.known_buyer ? (
              <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' sx={{ mt: 0.75, gap: 0.75 }}>
                <Chip size='small' variant='outlined' color='success' label={`${buyer.overall_delivered ?? 0} delivered`} />
                <Chip size='small' variant='outlined' color={(buyer.overall_rto ?? 0) > 0 ? 'error' : 'default'} label={`${buyer.overall_rto ?? 0} RTO`} />
                {((buyer.this_pincode_delivered ?? 0) + (buyer.this_pincode_rto ?? 0)) > 0 && (
                  <Typography variant='caption' color='text.secondary'>
                    {buyer.this_pincode_delivered ?? 0} delivered / {buyer.this_pincode_rto ?? 0} RTO at this pincode
                  </Typography>
                )}
                {buyer.last_order_date && (
                  <Typography variant='caption' color='text.secondary'>
                    · last order {new Date(buyer.last_order_date).toLocaleDateString('en-IN')}
                  </Typography>
                )}
              </Stack>
            ) : (
              <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                New buyer — no prior delivery history for this brand.
              </Typography>
            )}
          </Box>
        )}

        {hygieneBlock && (
          <Typography variant='body2' color='error.main' fontWeight={700} sx={{ mt: 2 }}>
            Hygiene block — the model did not run; the score was forced by a deterministic rule.
          </Typography>
        )}

        {contributions.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', letterSpacing: '.04em' }}>
              Parameter weightage
            </Typography>
            <Box sx={{ mt: 0.75 }}>
              {contributions.map((c) => <ContributionRow key={c.code} c={c} />)}
            </Box>
            <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 0.5 }}>
              % = share of total contribution · (Δ) = raw log-odds · <Box component='span' sx={{ color: '#e53935' }}>red</Box> raises / <Box component='span' sx={{ color: '#43a047' }}>green</Box> lowers risk
            </Typography>
          </Box>
        )}

        {ACTION[netra.band] && (
          <Box sx={{ mt: 2, p: 1.25, borderRadius: 1, bgcolor: (t) => (b3.color === 'error' ? t.palette.error.light + '22' : b3.color === 'warning' ? t.palette.warning.light + '22' : t.palette.success.light + '22') }}>
            <Typography variant='body2' fontWeight={600}>{ACTION[netra.band]}</Typography>
            <Typography variant='caption' color='text.secondary'>Advisory only — NETRA does not hold or cancel orders.</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default RtoRiskCard
